import { createFileRoute } from "@tanstack/react-router";
import MarcaDetalhe from "@/pages/MarcaDetalhe";
import { supabase } from "@/integrations/supabase/client";
import { seoHead } from "@/lib/seoHead";
import { buildBrandSeoTitle, buildBrandSeoDescription } from "@/lib/brandSeoTitle";

interface LoaderBrand {
  name: string;
  slug: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_canonical_url?: string | null;
  seo_noindex?: boolean | null;
  seo_prefix_promocional?: boolean | null;
  logo_url?: string | null;
  category?: { name?: string | null } | null;
}

/**
 * SSR da página de marca: o loader roda no servidor na primeira visita
 * (logo, título, card de doação e cupons já vêm no HTML) e no cliente nas
 * navegações internas. Usa a MESMA queryKey de useBrandPageData, então o
 * hook encontra o dado no cache e não refaz a chamada.
 */
export const Route = createFileRoute("/marca/$slug")({
  loader: async ({ params, context }) => {
    try {
      const payload = await context.queryClient.ensureQueryData({
        queryKey: ["brand-page-data", params.slug],
        queryFn: async () => {
          const { data, error } = await (supabase.rpc as CallableFunction)(
            "get_brand_page_data",
            { p_slug: params.slug },
          );
          if (error) throw error;
          return data ?? null;
        },
        staleTime: 5 * 60 * 1000,
      });
      const typed = payload as
        | { brand?: LoaderBrand | null; active_coupons?: unknown[] }
        | null;
      return {
        brand: typed?.brand ?? null,
        coupons: (typed?.active_coupons ?? []) as never[],
      };
    } catch {
      // Falha no prefetch nunca quebra a página: o hook refaz a consulta no navegador.
      return { brand: null, coupons: [] as never[] };
    }
  },
  // Mesmos valores do usePageSeo da página, agora no HTML do servidor.
  head: ({ loaderData, params }) => {
    const brand = loaderData?.brand;
    if (!brand) {
      return seoHead({
        title: "Loja indisponível | Shopping do Bem",
        description:
          "Esta loja não está disponível no momento. Veja outras lojas parceiras do Shopping do Bem.",
        noindex: true,
      });
    }
    const coupons = loaderData?.coupons ?? [];
    const title =
      brand.seo_title ||
      buildBrandSeoTitle(brand.name, coupons, brand.seo_prefix_promocional ?? undefined);
    const description =
      brand.seo_description ||
      buildBrandSeoDescription(
        brand.name,
        brand.category?.name ?? undefined,
        coupons,
        brand.seo_prefix_promocional ?? undefined,
      );
    return seoHead({
      title,
      description,
      canonical:
        brand.seo_canonical_url ||
        `https://shoppingdobem.com.br/marca/${brand.slug || params.slug}`,
      noindex: brand.seo_noindex ?? false,
      ogImage: brand.logo_url ?? null,
    });
  },
  component: MarcaDetalhe,
});
